import React, { useState, useRef, useContext, useEffect } from "react";
import styled from "styled-components";
import { ProfileContext } from "../contexts/ProfileContext";
import { OrganizationContext } from "../contexts/OrganizationContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "../components/loading-spinner";
import * as XLSX from "xlsx";
import { useReactToPrint } from "react-to-print";
import { ChevronDown } from "lucide-react";
import PastoralPagination from "../components/pastoral-care/components/pagination";
import OrganizationNameButton from "../components/organization-name-button";

// ========== DATE HELPERS ==========
const formatDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getYTDRange = () => ({
  start: `${new Date().getFullYear()}-01-01`,
  end: formatDate(new Date()),
});
const getAllRange = () => ({
  start: "2015-01-01",
  end: formatDate(new Date()),
});
const getMonthRange = () => {
  const now = new Date();
  return {
    start: formatDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    end: formatDate(now),
  };
};

const VolunteerReports = () => {
  // ========== STATE ==========
  const [dataType, setDataType] = useState("ytd");
  const [exportOption, setExportOption] = useState("");
  const [isExportSelectOpen, setIsExportSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState(getYTDRange);
  const [currentPage, setCurrentPage] = useState(1);
  const [customButtonRect, setCustomButtonRect] = useState(null);

  // State for organization filter
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedOrganizationName, setSelectedOrganizationName] = useState("");
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);

  // ========== CONSTANTS ==========
  const itemsPerPage = 10;

  // ========== REFS ==========
  const exportSelectRef = useRef(null);
  const volunteerReportPrintRef = useRef();
  const datePickerRef = useRef(null);
  const customButtonRef = useRef(null);

  // ========== CONTEXT ==========
  const {
    organization,
    worker,
    //organizationNumber,
    getVolunteerHours,
    organizationFirebaseId,
  } = useContext(ProfileContext);
  const { nameClicked, organizations } = useContext(OrganizationContext);

  const queryClient = useQueryClient();
  const organizationName = worker?.organizationName;

  // Check if user is super_admin or admin
  const isAdminOrSuperAdmin =
    worker?.role === "super_admin" || worker?.role === "admin";

  // Check if super_admin is viewing as subscriber (nameClicked is not empty)
  const isSuperAdminViewingAsSubscriber =
    worker?.role === "super_admin" && nameClicked !== "";

  // Determine if organization dropdown should be shown
  const showOrganizationDropdown =
    isAdminOrSuperAdmin && !isSuperAdminViewingAsSubscriber;

  // Get dynamic organizations list from context
  const organizationsList = organizations || [];

  const VOLUNTEER_TABLE_COLUMNS =
    (worker && worker.role === "subscriber") || nameClicked !== ""
      ? [
          { key: "agent", label: "Agent Name" },
         // { key: "total_handling_time", label: "Total Handling Time" },
          { key: "total_calling_time", label: "Total Calling Time" },
          { key: "handled_calls", label: "Handled Calls" },
        ]
      : [
          { key: "agent", label: "Agent Name" },
          { key: "organization", label: "Organization" },
          { key: "total_handling_time", label: "Total Handling Time" },
          { key: "total_calling_time", label: "Total Calling Time" },
          { key: "handled_calls", label: "Handled Calls" },
        ];

  // ========== QUERY ==========
  const {
    data: volunteerHoursData,
    isPending: isVolunteerHoursPending,
    isError: isVolunteerHoursError,
  } = useQuery({
    queryKey: ["volunteerHours", { organizationFirebaseId, ...selectedRange }],
    queryFn: () => getVolunteerHours(selectedRange.start, selectedRange.end),
    enabled: !!organizationName,
  });

  const volunteerTableData = volunteerHoursData?.original?.data ?? [];

  const getFilteredData = () => {
    if (isSuperAdminViewingAsSubscriber) {
      const filtered = volunteerTableData.filter((row) => {
        return row.organization;
      });
      return filtered;
    }

    if (!selectedOrganizationId || selectedOrganizationId === "") {
      // Show all organizations
      return volunteerTableData;
    }

    // Filter by selected organization name
    const selectedOrg = organizationsList.find(
      (org) => org.id === selectedOrganizationId,
    );
    if (!selectedOrg) return volunteerTableData;

    return volunteerTableData.filter(
      (row) => row.organization === selectedOrg.name,
    );
  };

  const filteredTableData = getFilteredData();

  // ========== FILTER HANDLERS ==========
  const handleFilterChange = (type) => {
    setDataType(type);
    setCurrentPage(1);
    if (type === "ytd") setSelectedRange(getYTDRange());
    if (type === "all") setSelectedRange(getAllRange());
    if (type === "monthly") setSelectedRange(getMonthRange());
    if (type === "custom") {
      setShowDatePicker(true);
      // Get the position of the custom button to align dropdown
      if (customButtonRef.current) {
        const rect = customButtonRef.current.getBoundingClientRect();
        setCustomButtonRect(rect);
      }
    }
  };

  const handleApplyCustomDates = () => {
    if (!tempStartDate || !tempEndDate) {
      alert("Please select both start and end dates.");
      return;
    }
    setSelectedRange({ start: tempStartDate, end: tempEndDate });
    setCurrentPage(1);
    setShowDatePicker(false);
    setTempStartDate("");
    setTempEndDate("");
  };

  const handleCancelCustomDates = () => {
    setShowDatePicker(false);
    setTempStartDate("");
    setTempEndDate("");
    setDataType("ytd");
    setSelectedRange(getYTDRange());
  };

  const handleOrganizationSelect = (orgId, orgName) => {
    setSelectedOrganizationId(orgId);
    setSelectedOrganizationName(orgName);
    setIsOrgDropdownOpen(false);
    setCurrentPage(1);
  };

  // ========== EFFECTS ==========
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(e.target)
      ) {
        setIsOrgDropdownOpen(false);
      }
    };
    if (showDatePicker || isOrgDropdownOpen)
      document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker, isOrgDropdownOpen]);

  useEffect(() => {
    if (organization?.id) {
      queryClient.invalidateQueries({ queryKey: ["volunteerHours"] });
    }
  }, [organization?.id, queryClient]);

  useEffect(() => {
    const selectEl = exportSelectRef.current;
    if (!selectEl) return;
    const handleMouseDown = () => setIsExportSelectOpen((prev) => !prev);
    const handleBlur = () => setIsExportSelectOpen(false);
    selectEl.addEventListener("mousedown", handleMouseDown);
    selectEl.addEventListener("blur", handleBlur);
    return () => {
      selectEl.removeEventListener("mousedown", handleMouseDown);
      selectEl.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    if (exportOption === "print") handleVolunteerPrint();
    if (exportOption === "export") handleExportVolunteerExcel();
  },
      // eslint-disable-next-line
      [exportOption]);

  // ========== EXPORT ==========
  const handleVolunteerPrint = useReactToPrint({
    content: () => volunteerReportPrintRef.current,
    documentTitle: `Volunteer_Hours_Report_${selectedOrganizationName || organizationName || ""}`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  const handleExportVolunteerExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const excelRows =
        filteredTableData.length > 0
          ? [
              ["Volunteer Hours Report"],
              [
                `Organization: ${selectedOrganizationName || "All Organizations"}`,
              ],
              [`Date Range: ${selectedRange.start} to ${selectedRange.end}`],
              [],
              VOLUNTEER_TABLE_COLUMNS.map((col) => col.label),
              ...filteredTableData.map((row) =>
                VOLUNTEER_TABLE_COLUMNS.map((col) => row[col.key] ?? "—"),
              ),
            ]
          : [["No volunteer data available"]];

      const volunteerWorksheet = XLSX.utils.aoa_to_sheet(excelRows);
      volunteerWorksheet["!cols"] = VOLUNTEER_TABLE_COLUMNS.map(() => ({
        wch: 22,
      }));
      XLSX.utils.book_append_sheet(wb, volunteerWorksheet, "VolunteerHours");
      XLSX.writeFile(
        wb,
        `Volunteer_Hours_Report_${selectedOrganizationName || "All"}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      setExportOption("");
    } catch (error) {
      console.error("Volunteer Excel export failed:", error);
      alert("Failed to export volunteer report");
      setExportOption("");
    }
  };

  const paginatedDetails = filteredTableData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Get the display text for the organization button
  const getOrganizationButtonText = () => {
    if (selectedOrganizationName) {
      return selectedOrganizationName;
    }
    return "ORGANIZATIONS";
  };

  // Get dropdown position style based on custom button
  const getDropdownPositionStyle = () => {
    if (!customButtonRect) return { top: "100%", left: "0" };

    // Calculate position relative to the custom button
    const offsetLeft = customButtonRect.left;
    const offsetTop = customButtonRect.bottom + window.scrollY;

    return {
      position: "fixed",
      top: `${offsetTop + 8}px`,
      left: `${offsetLeft}px`,
    };
  };

  // ========== RENDER ==========
  return (
    <Wrapper>
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}
        <TitleText>Volunteer Reports</TitleText>
      </TopSection>

      <ToggleButtonContainer>
        <ToggleContent>
          {/* Left side with filter tabs and organization dropdown */}
          <LeftSideGroup>
            <ToggleButtonGroup>
              <ToggleButton
                active={dataType === "ytd"}
                onClick={() => handleFilterChange("ytd")}
              >
                YEAR TO DATE
              </ToggleButton>
              <ToggleButton
                active={dataType === "all"}
                onClick={() => handleFilterChange("all")}
              >
                ALL DATA
              </ToggleButton>
              <ToggleButton
                active={dataType === "monthly"}
                onClick={() => handleFilterChange("monthly")}
              >
                CURRENT MONTH
              </ToggleButton>
              <ToggleButton
                ref={customButtonRef}
                active={dataType === "custom"}
                onClick={() => handleFilterChange("custom")}
              >
                CUSTOM DATE RANGE
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Organization Dropdown - Only for super_admin and admin */}
            {showOrganizationDropdown && (
              <OrganizationFilterWrapper ref={orgDropdownRef}>
                <OrganizationFilterButton
                  active={selectedOrganizationId !== ""}
                  onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                >
                  {getOrganizationButtonText()}
                  <ChevronIcon isOpen={isOrgDropdownOpen}>
                    <ChevronDown size={14} />
                  </ChevronIcon>
                </OrganizationFilterButton>

                {isOrgDropdownOpen && (
                  <OrganizationDropdown>
                    <OrganizationDropdownItem
                      onClick={() => handleOrganizationSelect("", "")}
                      isSelected={selectedOrganizationId === ""}
                    >
                      All Organizations
                    </OrganizationDropdownItem>
                    {organizationsList.map((org) => (
                      <OrganizationDropdownItem
                        key={org.id}
                        onClick={() =>
                          handleOrganizationSelect(org.id, org.name)
                        }
                        isSelected={selectedOrganizationId === org.id}
                      >
                        {org.name}
                      </OrganizationDropdownItem>
                    ))}
                  </OrganizationDropdown>
                )}
              </OrganizationFilterWrapper>
            )}
          </LeftSideGroup>

          {/* Right side with export dropdown */}
          <RightSideContainer>
            <ExportContainer>
              <SelectWrapper>
                <ExportSelect
                  ref={exportSelectRef}
                  value={exportOption}
                  onChange={(e) => setExportOption(e.target.value)}
                >
                  <option value="" disabled>
                    Export Options
                  </option>
                  <option value="print">Print PDF</option>
                  <option value="export">Export as Excel Sheet</option>
                </ExportSelect>
                <ArrowIcon isOpen={isExportSelectOpen}>
                  <ChevronDown />
                </ArrowIcon>
              </SelectWrapper>
            </ExportContainer>
          </RightSideContainer>
        </ToggleContent>

        {showDatePicker && (
          <DatePickerDropdown
            ref={datePickerRef}
            style={getDropdownPositionStyle()}
          >
            <DateInputGroup>
              <label>From (mm/dd/yyyy)</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                placeholder="mm/dd/yyyy"
              />
            </DateInputGroup>
            <DateInputGroup>
              <label>To (mm/dd/yyyy)</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                placeholder="mm/dd/yyyy"
              />
            </DateInputGroup>
            <DatePickerActions>
              <button onClick={handleApplyCustomDates}>Apply</button>
              <button onClick={handleCancelCustomDates}>Cancel</button>
            </DatePickerActions>
          </DatePickerDropdown>
        )}
      </ToggleButtonContainer>

      <PrintContainer ref={volunteerReportPrintRef}>
        <PdfOnlyHeader>
          <div className="main-title">VOLUNTEER REPORTS</div>
          {selectedOrganizationName && (
            <div className="sub-title">
              Organization: {selectedOrganizationName}
            </div>
          )}
        </PdfOnlyHeader>

        <ScreenOnlyTableContainer>
          <StyledTable>
            <thead>
              <tr>
                {VOLUNTEER_TABLE_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isVolunteerHoursPending ? (
                <tr>
                  <td colSpan={VOLUNTEER_TABLE_COLUMNS.length}>
                    <LoadingState>
                      <LoadingSpinner />
                    </LoadingState>
                  </td>
                </tr>
              ) : isVolunteerHoursError ? (
                <tr>
                  <td colSpan={VOLUNTEER_TABLE_COLUMNS.length}>
                    <EmptyState>Failed to load volunteer data.</EmptyState>
                  </td>
                </tr>
              ) : filteredTableData.length === 0 ? (
                <tr>
                  <td colSpan={VOLUNTEER_TABLE_COLUMNS.length}>
                    <EmptyState>
                      {selectedOrganizationName
                        ? `No volunteer data available for ${selectedOrganizationName}.`
                        : "No volunteer data available."}
                    </EmptyState>
                  </td>
                </tr>
              ) : (
                paginatedDetails.map((volunteerRow, idx) => (
                  <tr key={idx}>
                    {VOLUNTEER_TABLE_COLUMNS.map((col) => (
                      <td key={col.key}>{volunteerRow[col.key] ?? "—"}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </StyledTable>
        </ScreenOnlyTableContainer>

        <PrintOnlyTableContainer>
          <StyledTable>
            <thead>
              <tr>
                {VOLUNTEER_TABLE_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTableData.map((volunteerRow, idx) => (
                <tr key={idx}>
                  {VOLUNTEER_TABLE_COLUMNS.map((col) => (
                    <td key={col.key}>{volunteerRow[col.key] ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </PrintOnlyTableContainer>
      </PrintContainer>

      {filteredTableData.length > itemsPerPage && (
        <StyledPagination>
          <PastoralPagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            total_count={filteredTableData.length}
            handlePageChange={setCurrentPage}
          />
        </StyledPagination>
      )}
    </Wrapper>
  );
};

export default VolunteerReports;

// ========== STYLED COMPONENTS ==========

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
`;

const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }

    .sub-title {
      text-align: center;
      font-family: "Space Grotesk", sans-serif;
      font-size: 0.9rem;
      color: #106271;
      margin-top: 8px;
    }
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

//const SelectedOrgInfo = styled.div`
//  background: #e8f4f5;
//  padding: 8px 16px;
//  border-radius: 8px;
//  margin-bottom: 16px;
//  font-family: "Space Grotesk", sans-serif;
//  font-size: 13px;
//  color: #106271;
//  text-align: center;

//  strong {
//    color: #ec3310;
//    font-weight: 600;
//  }

//  @media (max-width: 768px) {
//    margin: 0 1rem 16px 1rem;
//  }
//`;

const PrintContainer = styled.div`
  width: 100%;
  display: block;
  @media print {
    width: 100%;
    display: block;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  padding: 2rem;
  background-color: #f3f9fa;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  min-width: 800px;
  background-color: #fff;

  th,
  td {
    text-align: center;
    padding: 12px;
    border: 1px solid #00323b;
    white-space: nowrap;
    color: #178b96;
  }

  thead th {
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8f9fa;
    color: #178b96;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  @media (max-width: 1024px) {
    min-width: 700px;

    th,
    td {
      padding: 10px;
      font-size: 14px;
    }
  }

  @media (max-width: 768px) {
    min-width: 600px;

    th,
    td {
      padding: 8px;
      font-size: 13px;
    }
  }

  @media (max-width: 480px) {
    min-width: 500px;

    th,
    td {
      padding: 6px;
      font-size: 12px;
    }
  }
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding-top: 10px;

  @media (max-width: 480px) {
    padding-top: 8px;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-family: "Space Grotesk";
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 30px 15px;
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 1rem;
  }
`;

const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const LeftSideGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
    max-width: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center;

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;
    font-size: 14px;
    letter-spacing: 0.15em;
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;
    font-size: 14px;
    letter-spacing: 0.25em;
    padding: 0 8px;
  }
`;

const OrganizationFilterWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const OrganizationFilterButton = styled.button`
  width: auto;
  min-width: 160px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center;

  background: #ff6b35;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;

  color: white;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:hover {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;
    justify-content: center;
    gap: 12px;
    border-radius: 50px;
  }

  @media (max-width: 480px) {
    height: 36px;
    font-size: 12px;
    padding: 0 8px;
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;
  transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};

  svg {
    width: 14px;
    height: 14px;
    color: white;
  }
`;

const OrganizationDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 8px 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    min-width: 250px;
  }
`;

const OrganizationDropdownItem = styled.div`
  padding: 10px 16px;
  font-family: "Space Grotesk", sans-serif;
  font-size: 13px;
  color: ${({ isSelected }) => (isSelected ? "#FF6B35" : "#333")};
  background: ${({ isSelected }) => (isSelected ? "#FFF0E8" : "transparent")};
  font-weight: ${({ isSelected }) => (isSelected ? "600" : "400")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fff0e8;
    color: #ff6b35;
  }
`;

const RightSideContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const SelectWrapper = styled.div`
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 172px;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const DatePickerDropdown = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50% !important;
    transform: translateX(-50%);
    min-width: 260px;
  }

  @media (max-width: 480px) {
    min-width: 260px;
    padding: 12px;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 12px;
    color: #666;
    font-weight: 500;
    font-family: "Space Grotesk";
    letter-spacing: 0.5px;
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;
    transition: all 0.2s;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const ScreenOnlyTableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #dee2e6;
  margin-bottom: 20px;
  max-height: 1000px;

  @media print {
    display: none;
  }

  @media (max-width: 768px) {
    max-height: 500px;
    margin-bottom: 16px;
  }
  @media (max-width: 480px) {
    max-height: 400px;
    margin-bottom: 12px;
  }
`;

const PrintOnlyTableContainer = styled.div`
  display: none;

  @media print {
    display: block;
    width: 100%;
    overflow: visible;
    border: 1px solid #dee2e6;
  }
`;
